import React from 'react';
import * as _ from 'lodash';
import Helmet from 'react-helmet';

import { WorkersApi } from '../services/workers/WorkersApi';
import CompanyService from '../services/Companies';

import Confirmation from '../components/modals/common/Confirmation';
import Information from '../components/modals/common/Information';
import { WorkersCaptureTable } from '../components/tables/WorkersCapture';

import SessionStorageService from '../services/utilities/SessionStorageService';

import ImageUtility from '../utilities/imageUtility';
import masterSlideLoader from '../utilities/masterSlideLoader';
import { isNullOrUndefinedOrEmptyStr } from '../utilities/stringUtil';
import {
  API,
  HEADER_CONSTANTS,
  STATUS_CODE,
  TIME_OUT_TWO_SECOND,
} from '../utilities/constants';
import { messageUtility } from '../utilities/messageUtility';
import { MasterPageHeader } from '../components/PageHeader';
import AuthProvider, { needTokenUpdate } from '../utilities/authProvider';
import { setWaitTime } from '../utilities/commonUtil';
import Loader from '../components/Loader';
import EventMg, { EventType } from '../utilities/EventManager';

// ダイアログ名
export const DIALOG_NAME = {
  registerConfirm: 'registerConfirm', // 登録確認
  registerDone: 'registerDone', // 登録完了
  dynamicMessage: 'dynamicMessage', // 動的メッセージ
};

const JOB_STATUSES = {
  PRE_EXECUTING: 0,
  EXECUTING: 1,
  EXECUTED: 2,
};

const MONITORING_TIME_OUT = 3 * 60 * 1000;

/**
 * 作業員データ取り込み画面
 *  「作業員をExcelファイルから取り込む」
 * 画面遷移
 *  1.  協力会社管理者ユーザー>>自社作業員一覧>>「Excelファイルから取り込む」ボタン
 *  2.  協力会社管理者ユーザー>>行登録会社一覧>>作業員一覧>>「Excelファイルから取り込む」ボタン
 */
export class WorkersCapture extends React.Component {
  constructor(props) {
    super(props);

    this.selectedPartnerCompanyId = SessionStorageService.getSessionSelectedPartnerCompanyId();
    if (isNullOrUndefinedOrEmptyStr(this.selectedPartnerCompanyId)) {
      throw new Error('WorkersCapture.jsx sessionSelectedPartnerCompanyId');
    }

    this.state = {
      workerLists: [], // 作業員情報リスト
      workerErrorMessages: [], // 作業員情報エラーメッセージ
      workerSkipMessages: [], // 作業員情報スキップメッセージ
      selectedPartnerCompanyName: '', // 選択協力会社名
      visibleDialogName: '', // 開閉用ダイアログ名
      dialogMessage: '', // ダイアログ用メッセージ
      isFileSelect: false, // ファイル選択フラグ true: ファイル選択済み, false: ファイル未選択
      isFileError: false, // ファイルエラーフラグ true: エラー有り, false: エラー無し
      isRegisterEnable: false, // 登録可能フラグ true: 登録可能, false, 登録不可
      isLoading: false, // ロード中フラグ true: ロード中, false: ロード完了
      version: '',
    };

    this.fileButtonRef = React.createRef(); // ファイル選択ボタン要素

    this.selectFileCheck = this.selectFileCheck.bind(this); // 選択ファイルチェック
    this.workersBatchRegister = this.workersBatchRegister.bind(this); // 作業員一括登録
    this.handleVisibleDialog = this.handleVisibleDialog.bind(this); // ダイアログ開閉設定
    this.closePage = this.closePage.bind(this); // 画面クローズ

    this.isRegisterWaiting = false; //Excel取込処理待機中 連打防止用
    this.isPreviewWaiting = false;
    this.monitoringStartTime = null;
    this.workerBulkImportManagementId = null;
  }

  /**
   * React Component LifeCycle
   * マウントが行われた直後
   * */
  async componentDidMount() {
    const response = await CompanyService.show(this.selectedPartnerCompanyId);
    if (response.result) {
      this.setState({
        selectedPartnerCompanyName: response.data.entities.company.name,
      });
    }
  }

  /**
   * monitoring status of job by pooling api
   * @param {string} workerBulkImportManagementId
   * @param {boolean} isWorkerPreview
   * @returns {Promise<unknown>}
   */
  monitoringJobStatus = async (
    workerBulkImportManagementId,
    isWorkerPreview = true
  ) => {
    await setWaitTime(TIME_OUT_TWO_SECOND);
    const response = await WorkersApi.fetchWorkerImportBatch({
      worker_bulk_import_management_id: workerBulkImportManagementId,
      from_worker_preview: isWorkerPreview,
    });
    if (!response.result) {
      throw response;
    }
    const status = _.get(
      response,
      'data.entities.worker_bulk_import_management.status'
    );
    switch (status) {
      case JOB_STATUSES.PRE_EXECUTING:
        const now = performance.now();
        if (now - this.monitoringStartTime > MONITORING_TIME_OUT) {
          throw {
            data: {
              status: STATUS_CODE.badRequest,
              message: messageUtility.getMessage('E-000-055'),
            },
          };
        }
        return await this.monitoringJobStatus(
          workerBulkImportManagementId,
          isWorkerPreview
        );
      case JOB_STATUSES.EXECUTING:
        return await this.monitoringJobStatus(
          workerBulkImportManagementId,
          isWorkerPreview
        );
      case JOB_STATUSES.EXECUTED:
        return response;
      default:
        throw response;
    }
  };

  /**
   * @description 選択ファイルチェック
   * @param {*} file 選択ファイル
   */
  async selectFileCheck(file) {
    this.fileButtonRef.current.value = '';
    const isValidType = await ImageUtility.approvalFileType(file, ['xlsx']);
    const isValidSize = await ImageUtility.fileSizeCheck(file);

    if (!isValidType) {
      return this.handleVisibleDialog(
        DIALOG_NAME.dynamicMessage,
        '選択可能なファイル形式は「xlsx」ファイルです。'
      );
    }

    if (!isValidSize) {
      return this.handleVisibleDialog(
        DIALOG_NAME.dynamicMessage,
        messageUtility.getMessage('E-000-044')
      );
    }

    if (this.isPreviewWaiting) {
      return;
    }
    this.isPreviewWaiting = true;

    const form = new FormData();
    form.append('file', file);
    if (this.workerBulkImportManagementId) {
      form.append(
        'worker_bulk_import_management_id',
        this.workerBulkImportManagementId
      );
    }
    this.setState({
      workerLists: [],
      workerErrorMessages: [],
      workerSkipMessages: [],
      isFileSelect: true,
      isFileError: false,
      isRegisterEnable: false,
      isLoading: true,
    });

    try {
      Loader.start();
      const response = await WorkersApi.captureCheck(
        this.selectedPartnerCompanyId,
        form
      );
      if (!response.result) {
        throw response;
      }
      const workerBulkImportManagementId = _.get(
        response,
        'data.entities.worker_bulk_import_management_id'
      );
      this.workerBulkImportManagementId = workerBulkImportManagementId;
      this.monitoringStartTime = performance.now();
      const monitoringResponse = await this.monitoringJobStatus(
        workerBulkImportManagementId
      );
      const {
        rows: workerLists,
        error_messages: workerErrorMessages,
        skip_messages: workerSkipMessages,
        version,
      } = _.get(monitoringResponse, 'data.entities.result_data');
      const message = _.get(
        monitoringResponse,
        'data.entities.worker_bulk_import_management.message'
      );
      const isFileValid = _.isEmpty(message);
      this.setState({
        workerLists,
        workerErrorMessages,
        workerSkipMessages,
        isRegisterEnable: isFileValid,
        isFileError: !isFileValid,
        version,
      });
    } catch (error) {
      this.handleError(error);
      this.setState({ isFileSelect: false });
    } finally {
      Loader.done();
      this.isPreviewWaiting = false;
      this.setState({
        isLoading: false,
      });
    }
  }

  /**
   * @description 作業員一括登録
   */
  async workersBatchRegister() {
    // 連打防止 待機中の場合は何もしない
    if (this.isRegisterWaiting) {
      return;
    }
    this.isRegisterWaiting = true;

    try {
      Loader.start();
      const param = {
        worker_bulk_import_management_id: this.workerBulkImportManagementId,
      };
      const response = await WorkersApi.batchRegister(
        this.selectedPartnerCompanyId,
        param
      );
      if (!response.result) {
        throw response;
      }
      this.monitoringStartTime = performance.now();
      const monitoringResponse = await this.monitoringJobStatus(
        this.workerBulkImportManagementId,
        false
      );
      EventMg.trigger(EventType.FETCH_CONSISTENCY_STATUS);
      const message = _.get(
        monitoringResponse,
        'data.entities.worker_bulk_import_management.message'
      );
      if (_.isEmpty(message)) {
        this.workerBulkImportManagementId = null;
        this.handleVisibleDialog(DIALOG_NAME.registerDone);
      } else {
        const {
          rows: workerLists,
          error_messages: workerErrorMessages,
          skip_messages: workerSkipMessages,
        } = _.get(monitoringResponse, 'data.entities.result_data');
        this.handleVisibleDialog('');
        this.setState({
          workerLists,
          workerErrorMessages,
          workerSkipMessages,
          isFileError: true,
          isRegisterEnable: false,
        });
      }
    } catch (error) {
      this.handleError(error, true);
    } finally {
      Loader.done();
      this.isRegisterWaiting = false;
    }
  }

  /**
   * handle the error of bulk import
   * @param {object} error
   * @param {boolean} isShowResponseMessage
   */
  handleError = (error, isShowResponseMessage = false) => {
    let errorMessage = 'エラーが発生しました。';
    if (
      error.data &&
      error.data.status === STATUS_CODE.badRequest &&
      error.data.message
    ) {
      const { message } = error.data;
      if (message.includes(messageUtility.getMessage('E-000-052'))) {
        errorMessage =
          'ブラウザによって処理がリトライされました。処理が失敗した可能性があります。一覧画面に戻って入力内容が反映されているかご確認ください。このエラーが何度も発生し、成功しない場合には一度にアップロードする作業員の人数を減らして再度実行してください。';
      } else if (
        isShowResponseMessage ||
        message === messageUtility.getMessage('E-000-055')
      ) {
        errorMessage = message;
      }
    }
    this.handleVisibleDialog(DIALOG_NAME.dynamicMessage, errorMessage);
  };

  /**
   * @description ダイアログ開閉設定
   * @param {string} visibleDialogName ダイアログ名(DIALOG_NAME)
   *                                   ※ 空文字の場合は、ダイアログを閉じる
   * @param {string} dialogMessage ダイアログ用メッセージ
   */
  handleVisibleDialog(visibleDialogName, dialogMessage = '') {
    this.setState({ visibleDialogName, dialogMessage });
  }

  /**
   * @description 画面クローズ
   */
  closePage() {
    masterSlideLoader.show('Workers', { state: {} });
  }

  /**
   * @description テンプレートをダウンロード
   */
  templateDownload = async () => {
    const companyId = this.selectedPartnerCompanyId;
    let token = SessionStorageService.getToken();
    if (needTokenUpdate(token)) {
      token = await AuthProvider.instance.silentTokenUdate();
    }
    const apiPath = `api/private/ccus/companies/${companyId}/workers/download_template.xlsx?excel_token=${token}`;

    window.location.href = `${API.endpoint}/${apiPath}`;
  };

  render() {
    const {
      workerLists,
      workerErrorMessages,
      workerSkipMessages,
      selectedPartnerCompanyName,
      visibleDialogName,
      dialogMessage,
      isFileSelect,
      isFileError,
      isRegisterEnable,
      isLoading,
      version,
    } = this.state;

    return (
      <div id='slide-view' className='master-slide-view show WorkersCapture'>
        <Helmet>
          <title>{HEADER_CONSTANTS.gatekeeper} - 作業員データ取り込み</title>
        </Helmet>
        <div className='master-slide-view__view'>
          <MasterPageHeader
            title={`${selectedPartnerCompanyName} 作業員一括取り込み`}
            onClose={this.closePage}
          />
          <div className='master-slide-view__table workers-capture'>
            <div className='master-slide-view__back-btn'>
              <button className='btn-text--sm' onClick={this.closePage}>
                <i className='i-back--blue i-2x vertical-middle' />
                {SessionStorageService.getItem(
                  'sessionPartnerCompanyIdOfPrimeCompany'
                ) === this.selectedPartnerCompanyId
                  ? '自社作業員一覧'
                  : `${selectedPartnerCompanyName} 作業員一覧`}
              </button>
            </div>
            <HeaderMenu
              onSelectFileCheck={this.selectFileCheck}
              onHandleVisibleDialog={this.handleVisibleDialog}
              isRegisterEnable={isRegisterEnable}
              fileButtonRef={this.fileButtonRef}
              templateDownload={this.templateDownload}
              previewWorkersBatchRegister={this.previewWorkersBatchRegister}
            />
            <WorkersCaptureAttention />
            {isFileError && (
              <ErrorMessagesForm workerErrorMessages={workerErrorMessages} />
            )}
            {!_.isEmpty(workerSkipMessages) && (
              <SkipMessagesForm workerSkipMessages={workerSkipMessages} />
            )}
            <WorkersCaptureTable
              visibleDialogName={visibleDialogName}
              workerLists={workerLists}
              isFileSelect={isFileSelect}
              isFileError={isFileError || !_.isEmpty(workerSkipMessages)}
              isLoading={isLoading}
              version={version}
            />
          </div>
        </div>

        {/* 登録確認ダイアログ */}
        <Confirmation
          visible={visibleDialogName === DIALOG_NAME.registerConfirm}
          title='この内容で取り込み'
          message={
            <p>
              表示中の内容で作業員の一括取り込みを行います。
              <br />
              よろしいですか？
            </p>
          }
          confirmButtonText='取り込み'
          cancelButtonText='戻る'
          onConfirm={this.workersBatchRegister}
          onCancel={() => this.handleVisibleDialog('')}
        />

        {/* 登録完了ダイアログ */}
        <Information
          visible={visibleDialogName === DIALOG_NAME.registerDone}
          title='作業員一括取り込み完了'
          message='作業員の一括取り込みが完了しました。'
          buttonText={messageUtility.getMessage('I-000-017')}
          multilineText={true}
          onClose={() => {
            this.handleVisibleDialog('');
            this.closePage();
          }}
          buttonClassName='btn--sub'
        />

        {/* 動的メッセージダイアログ */}
        <Information
          rootClassName='WorkersCapture__dialog-info'
          title={messageUtility.getMessage('E-001-402')}
          visible={visibleDialogName === DIALOG_NAME.dynamicMessage}
          message={dialogMessage}
          buttonText={messageUtility.getMessage('I-000-017')}
          onClose={() => this.handleVisibleDialog('')}
          buttonClassName='btn--sub'
          multilineText={true}
        />
      </div>
    );
  }
}

/**
 * @description ヘッダーメニュー
 * @param {function} onSelectFileCheck 選択ファイルチェック
 * @param {function} onHandleVisibleDialog ダイアログ開閉設定
 * @param {boolean} isRegisterEnable 登録可能フラグ true: 登録可能, false, 登録不可
 * @param {*} fileButtonRef ファイル選択ボタン要素
 */
const HeaderMenu = props => {
  const {
    onSelectFileCheck,
    onHandleVisibleDialog,
    isRegisterEnable,
    fileButtonRef,
    templateDownload,
  } = props;

  return (
    <div className='master-slide-view__menu WorkersCaptureHeaderMenu'>
      <ul className='btn-menu'>
        <li className='btn-menu__item sm--hidden conf-display-none'>
          <button
            className={`btn-fixedsize--effective ${!isRegisterEnable &&
              'btn-fixedsize--disabled'}`}
            disabled={!isRegisterEnable}
            onClick={() => onHandleVisibleDialog(DIALOG_NAME.registerConfirm)}
          >
            この内容で取り込み
          </button>
        </li>

        <div className='segment-line conf-display-none' />

        <li className='btn-menu__item padding-left'>
          <label htmlFor='workers-file' className='btn input-file'>
            ファイルを選択
            <input
              id='workers-file'
              type='file'
              ref={fileButtonRef}
              accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              onChange={e => onSelectFileCheck(e.target.files[0])}
            />
          </label>
        </li>
        <li className='btn-menu__item padding-left'>
          <button className='btn--sub' onClick={templateDownload}>
            テンプレートをダウンロード
          </button>
        </li>
      </ul>
    </div>
  );
};

/**
 * @description エラーメッセージフォーム
 * @param {array} workerErrorMessages 作業員情報エラーメッセージ
 */
const ErrorMessagesForm = props => {
  const { workerErrorMessages } = props;

  // prettier-ignore
  const labelsMap = {
    'カナ姓': 'セイ',
    'カナ名': 'メイ',
    'Fax': 'FAX',
    '雇入時教育年月日の日付': '雇入時教育年月日',
    '血液型が': 'ABO血液型が',
    '血液型Rhが': 'Rh血液型が',
    '緊急連絡先_氏名': '(緊急連絡先) 連絡先氏名',
    '緊急連絡先_続柄': '(緊急連絡先) 続柄',
    '緊急連絡先_郵便番号': '(緊急連絡先) 郵便番号',
    '緊急連絡先_都道府県': '(緊急連絡先) 都道府県',
    '緊急連絡先_市区町村': '(緊急連絡先) 市区町村',
    '緊急連絡先_丁目・番地': '(緊急連絡先) 丁目・番地',
    '緊急連絡先_建物名・部屋番号等': '(緊急連絡先) 建物名・部屋番号等',
    '緊急連絡先_電話番号': '(緊急連絡先) 電話番号',
    '技能士免許取得年月日': '技能士取得年月日',
    '(技能講習)受講年月日': '(技能講習) 受講年月日',
    '(特別教育)受講年月日': '(特別教育) 受講年月日',
    '(安全衛生講習)受講年月日': '(安全衛生講習) 受講年月日',
    '健康保険_加入状況': '(健康保険) 加入状況',
    '健康保険_適用除外理由': '(健康保険) 適用除外理由',
    '健康保険_健康保険種類': '(健康保険) 健康保険種類',
    '健康保険_健康保険者名称': '(健康保険) 健康保険者名称',
    '（健康保険）満了年月日': '(健康保険) 満了年月日',
    '健康保険_有効期限': '(健康保険) 有効期限',
    '年金保険_加入状況': '(年金) 加入状況',
    '年金保険_適用除外理由': '(年金) 適用除外理由',
    '年金保険_年金種類名称': '(年金) 年金種類名称',
    '年金保険_基礎年金番号': '(年金) 基礎年金番号',
    '年金保険_事業所整理記号': '(年金) 事業所整理記号',
    '年金保険_事業所整理番号': '(年金) 事業所番号',
    '（年金）満了年月日': '(年金) 満了年月日',
    '年金_有効期限': '(年金) 有効期限',
    '年金保険_受給中フラグ': '(年金) 受給状況',
    '雇用保険_加入状況': '(雇用保険) 加入状況',
    '雇用保険_適用除外理由': '(雇用保険) 適用除外理由',
    '雇用保険_被保険者種類': '(雇用保険) 被保険者種類',
    '雇用保険_労働保険番号': '(雇用保険) 労働保険番号',
    '雇用保険_被保険者番号': '(雇用保険) 被保険者番号',
    '失業保険_加入状況': '(失業保険・退職金共済) 失業保険加入状況',
    '建退共加入状況': '(失業保険・退職金共済) 建退共加入状況',
    '建設業退職金被共済者番号': '(失業保険・退職金共済) 建退共被共済者番号',
    '中退共加入状況': '(失業保険・退職金共済) 中退共加入状況',
    '中小企業退職金共済契約者番号': '(失業保険・退職金共済) 中退共契約者番号',
    '中小企業退職金被共済者番号': '(失業保険・退職金共済) 中退共被共済者番号',
    '労災保険特別加入_加入状況': '(労災保険特別加入) 加入状況',
    '労災保険種類': '(労災保険特別加入) 労災保険種類',
    '（健康診断）健康診断受診日の日付': '(健康診断) 健康診断受診日',
    '受診病院': '(健康診断) 受診病院',
    '視力_左': '(健康診断) 視力 左',
    '視力_右': '(健康診断) 視力 右',
    '血圧_上': '(健康診断) 血圧上下 上',
    '血圧_下': '(健康診断) 血圧上下 下',
    'X線所見': '(健康診断) X線所見',
    '糖検査': '(健康診断) 糖検査',
    '所見': '(健康診断) 所見',
    '特殊健康診断名': '(特殊健康診断) 特殊健康診断名',
    '（特殊健康診断）受診日の日付': '(特殊健康診断) 受診日',
    '特殊健康診断_じん肺管理区分': '(特殊健康診断) じん肺管理区分',
    '（特殊健康診断）受診日（じん肺）の日付': '(特殊健康診断) 受診日(じん肺)',
    '特殊健康診断_石綿管理区分': '(特殊健康診断) 石綿管理区分',
    '（特殊健康診断）受診日（石綿）の日付': '(特殊健康診断) 受診日(石綿)',
    '特殊健康診断_振動病管理区分': '(特殊健康診断) 振動病管理区分',
    '（特殊健康診断）受診日（振動病）の日付': '(特殊健康診断) 受診日(振動病)',
    '特殊健康診断_有機溶剤管理区分': '(特殊健康診断) 有機溶剤管理区分',
    '（特殊健康診断）受診日（有機溶剤）の日付':
      '(特殊健康診断) 受診日(有機溶剤)',
    '特殊健康診断_電離管理区分': '(特殊健康診断) 電離管理区分',
    '（特殊健康診断）受診日（電離）の日付': '(特殊健康診断) 受診日(電離)',
    '既往症有無の値': '(既往症) 既往症有無',
    '既往症有無(高血圧)の値': '(既往症) 高血圧',
    '既往症有無(低血圧)の値': '(既往症) 低血圧',
    '既往症有無(狭心症)の値': '(既往症) 狭心症',
    '既往症有無(糖尿病)の値': '(既往症) 糖尿病',
    '既往症有無(難聴)の値': '(既往症) 難聴',
    '既往症有無(夜盲症)の値': '(既往症) 夜盲症',
    '既往症有無(馬鞭式創傷綜合症)の値': '(既往症) 馬鞭式創傷綜合症',
    '既往症有無(腰痛)の値': '(既往症) 腰痛',
    '既往症有無(熱中症)の値': '(既往症) 熱中症',
    '既往症その他': '(既往症) その他',
    '健康保険_特段の理由' : '(健康保険) 特段の理由',
    '健康保険_加入予定年月' : '(健康保険) 加入予定年月',
    '年金保険_特段の理由' : '(年金) 特段の理由',
    '年金保険_加入予定年月' : '(年金) 加入予定年月',
    '雇用保険_特段の理由' : '(雇用保険) 特段の理由',
    '雇用保険_加入予定年月' : '(雇用保険) 加入予定年月',
    '年金保険_厚生年金事業所名' : '(年金) 厚生年金事業所名',
    'その他伝達事項など_メモ' : '(その他伝達事項など) メモ',
  };

  const formattedErrorMessages = _.map(workerErrorMessages, message => {
    let newMessage = message;
    _.each(labelsMap, (value, key) => {
      if (message.includes(key)) {
        newMessage = message.replace(key, value);
        return false;
      }
    });
    return newMessage;
  });

  return (
    <div className='master-slide-view__error-messages WorkersCaptureErrorMessagesForm'>
      <div className='master-slide-view__error-messages-title'>
        エラーのデータがあります。エラー箇所を確認し、ファイルを修正してください。
      </div>
      {_.map(formattedErrorMessages, (errorMessage, index) => (
        <div key={index} className='master-slide-view__error-messages-content'>
          {errorMessage}
        </div>
      ))}
    </div>
  );
};

const SkipMessagesForm = props => {
  const { workerSkipMessages } = props;

  return (
    <div className='master-slide-view__error-messages WorkersCaptureSkipMessagesForm'>
      <div className='master-slide-view__error-messages-title'>
        更新処理がスキップされる作業員がいます
      </div>
      <div className='master-slide-view__error-messages-contents'>
        {_.map(workerSkipMessages, (skipMessage, index) => (
          <div
            key={`skip_message_${index}`}
            className='master-slide-view__error-messages-content'
          >
            {skipMessage}
          </div>
        ))}
      </div>
    </div>
  );
};

const WorkersCaptureAttention = () => (
  <div className='WorkersCaptureAttention'>
    <ul>
      <li className='attention-item'>
        取り込みの際、同じ基本情報（姓、名、生年月日など）を持つ作業員がすでに登録されている場合はデータを上書きします。
      </li>
      <li className='attention-item'>
        一度に300件まで取り込めます。300件を超える場合はファイルを分割し、数回に分けて操作を行ってください。
      </li>
      <li className='attention-item'>
        一括取り込みでは基本情報の更新は行えません。変更したい場合は作業員一覧から個別に編集を行ってください。
      </li>
      <li className='attention-item'>
        ファイルが添付されている一部項目においては、作業員一括取り込み機能では更新できません。添付ファイルを含む情報を更新しようとした場合、当該作業員の一括取り込みは処理がスキップされます。更新が必要な場合、画面から直接更新を行ってください。対象の項目は以下です。
        <br />
        「基幹技能者」「技能士」「免許資格」「技能講習受講履歴」「特別教育受講履歴」「安全衛生教育受講履歴」「表彰履歴」
      </li>
      <li className='attention-item'>
        下の一覧には基本的な項目のみが表示されますが、登録・更新はExcelファイル上の全項目で可能です。
      </li>
    </ul>
  </div>
);



// WEBPACK FOOTER //
// ./src/pages/WorkersCapture.jsx